import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./UserSelect.module.scss";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import searchService from "../../store/services/search.service";
import { FilterCross, AddPersonIcon } from "../../modules/default-svg";
import {
    TeachingPopover,
    Avatar,
    Button,
    TeachingPopoverBody,
    TeachingPopoverHeader,
    TeachingPopoverSurface,
    TeachingPopoverTrigger,
    Skeleton,
    SkeletonItem,
    partitionAvatarGroupItems,
    AvatarGroupItem,
    AvatarGroupPopover,
} from "@fluentui/react-components";
import Input from "../controls/input/customInput/CustomInput";
import Spin from "../controls/spinner/Spin";
import HoverCard from "../hoverCard/HoverCard";
import { UserSearchRefType } from "../../helpers/utils";
import { AvatarGroup } from "flowbite-react";

export default function UserSelect({
    multiple = false,
    refType = null,
    refId = null,
    onChange,
    searchPlaceholder = "Search",
    disabled = false,
    value = [],
    className = "",
    style = {},
    label = "",
    buttonLabel = "Add Users",
    loading = false,
    errorMessage = "",
    isRequired = false,
    isAdminSearch = false,
    selectedUsersDisplayMode = "full", // "full" or "avatar-only"
    selectedUsersDisplayLimit = 5, // in "avatar-only" mode, how many selected users to show before collapsing into "+X more"
}) {
    const [openPopover, setIsPopoverOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const dispatch = useDispatch();
    const searchTimeoutRef = useRef(null);
    const pageSize = 10;

    useEffect(() => {
        const normalized = Array.isArray(value) ? value : (value ? [value] : []);
        setSelectedUsers(normalized);
    }, [value]);

    const buildQueryParams = () => {
        const params = { pageSize, pageNumber };
        if (refType === UserSearchRefType.Client && refId) params.clientId = refId;
        if (refType === UserSearchRefType.Project && refId) params.projectId = refId;
        if (refType === UserSearchRefType.Scope && refId) params.scopeId = refId;
        return params;
    };

    const filterUsers = (searchResults, selectedList) => {
        const selectedIds = selectedList.map((u) => u.id);
        return searchResults.filter((u) => !selectedIds.includes(u.id));
    };

    const fetchUsers = useCallback(
        (search = "", page = 1, append = false) => {
            // Don't search if refType is provided but refId is missing
            if (!isAdminSearch && refType >= 0 && !refId) {
                setUsers([]);
                setIsLoading(false);
                setIsLoadingMore(false);
                setHasMore(false);
                return;
            }

            if (!append) {
                setIsLoading(true);
                setUsers([]);
            } else {
                setIsLoadingMore(true);
            }

            const queryParams = { ...buildQueryParams(), pageNumber: page };

            searchService
                .Users(search, queryParams)
                .then((response) => {
                    const pagination = response.headers && response.headers["x-pagination"]
                        ? JSON.parse(response.headers["x-pagination"])
                        : null;

                    const filteredUsers = filterUsers(response.data || [], selectedUsers);

                    if (append) {
                        setUsers((prev) => [...prev, ...filteredUsers]);
                    } else {
                        setUsers(filteredUsers);
                    }

                    setHasMore(pagination?.HasNext || false);
                    setIsLoading(false);
                    setIsLoadingMore(false);
                })
                .catch((error) => {
                    setUsers([]);
                    setIsLoading(false);
                    setIsLoadingMore(false);
                    dispatch(
                        generalActions.showToast({
                            title: "Something went wrong. Please try again later.",
                            type: "error",
                        })
                    );
                });
        },
        [selectedUsers, refType, refId, pageSize]
    );

    const debouncedSearch = (value) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setPageNumber(1);
            fetchUsers(value, 1, false);
        }, 500);
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleOpenPopover = () => {
        setSearchTerm("");
        setPageNumber(1);
        fetchUsers("", 1, false);
        setIsPopoverOpen(true);
    };

    const handleAddUser = (user) => {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));

        if (multiple) {
            const updated = [...selectedUsers, user];
            setSelectedUsers(updated);
            onChange(updated);
        } else {
            setSelectedUsers([user]);
            onChange(user);
            setIsPopoverOpen(false);
        }
    };

    const handleRemoveUser = (user) => {
        const updated = selectedUsers.filter((u) => u.id !== user.id);
        setSelectedUsers(updated);
        onChange(multiple ? updated : null);
        setUsers((prev) => [...prev, user]);
    };

    const handleLoadMore = () => {
        const nextPage = pageNumber + 1;
        setPageNumber(nextPage);
        fetchUsers(searchTerm, nextPage, true);
    };

    const displayButtonLabel = multiple ? buttonLabel : buttonLabel.replace("Users", "User");

    return (
        <div className={`${styles.userSelectWrapper} ${className}`} style={style}>
            {label && (
                <label className={styles.label}>
                    {label}
                    {isRequired && <span className={styles.required}>*</span>}
                </label>
            )}

            {loading ? (
                <div className={styles.skeletonWrapper}>
                    <Skeleton>
                        <SkeletonItem style={{ width: "100%", height: "48px", borderRadius: "6px" }} />
                    </Skeleton>
                </div>
            ) : (
                <>
                    {multiple ? (
                        <div
                            className={`${styles.AssignResBox} ${errorMessage ? styles.error : ""}`}
                        >
                            <div className={styles.assignToItems}>
                                <TeachingPopover
                                    positioning="below-start"
                                    withArrow={false}
                                    open={openPopover}
                                    onOpenChange={(event, data) => !disabled && setIsPopoverOpen(data.open)}
                                >
                                    <TeachingPopoverTrigger>
                                        <Button
                                            className={styles.assignAvatarBtn}
                                            onClick={handleOpenPopover}
                                            disabled={disabled}
                                        >
                                            <Avatar
                                                icon={<AddPersonIcon color={"#342c82"} />}
                                                name=""
                                                color="transparent" />
                                            {buttonLabel && (
                                                <p className={`${styles.buttonLabel} text-xsm-400 ms-1 d-flex align-items-center`}>
                                                    {buttonLabel}
                                                </p>
                                            )}
                                        </Button>
                                    </TeachingPopoverTrigger>
                                    <TeachingPopoverSurface className={styles.assignAvatarSurface}>
                                        <TeachingPopoverBody className={styles.assignAvatarBody}>
                                            <Input placeholder={searchPlaceholder} handleChange={handleSearchChange} />
                                            {isLoading && users.length === 0 ? (
                                                <div className="d-flex justify-content-center mt-4">
                                                    <Spin />
                                                </div>
                                            ) : users.length > 0 ? (
                                                <div className={`${styles.userListWrapper} mt-2`}>
                                                    {users.map((user) => (
                                                        <div
                                                            className={`${styles.userRow} cursor-pointer`}
                                                            onClick={() => handleAddUser(user)}
                                                        >
                                                            <Avatar
                                                                color="colorful"
                                                                name={user.name}
                                                                image={{ src: user.image }}
                                                                size={26}
                                                            />
                                                            <p className="text-xsm-400">{user.name}</p>
                                                        </div>
                                                    ))}
                                                    {hasMore && (
                                                        <div className={styles.loadMoreRow}>
                                                            {isLoadingMore ? (
                                                                <Spin />
                                                            ) : (searchTerm && searchTerm != "" &&
                                                                <Button onClick={handleLoadMore} className={styles.loadMoreBtn}>
                                                                    Load more
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className={styles.noResults}>
                                                    <svg
                                                        width="24"
                                                        height="24"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M18.75 4A3.25 3.25 0 0 1 22 7.25v9.505a3.25 3.25 0 0 1-3.25 3.25H5.25A3.25 3.25 0 0 1 2 16.755V7.25a3.25 3.25 0 0 1 3.066-3.245L5.25 4h13.5Zm-9.497 8.5H5.75a.75.75 0 0 0-.743.648L5 13.25v.581c0 1.273.968 1.919 2.501 1.919 1.466 0 2.416-.59 2.496-1.755l.006-.163v-.582a.75.75 0 0 0-.649-.743l-.101-.007Zm2.997 0-1.416-.001c.086.182.142.381.161.591l.008.16v.582l-.01.254a2.85 2.85 0 0 1-.205.899c.108.01.22.015.337.015 1.052 0 1.793-.407 1.869-1.27l.006-.147v-.333a.75.75 0 0 0-.648-.743l-.102-.007-1.416-.001 1.416.001Zm6 0h-3l-.102.007a.75.75 0 0 0 0 1.486l.102.007h3l.102-.007a.75.75 0 0 0 0-1.486l-.102-.007ZM7.5 8.5a1.5 1.5 0 1 0 0 3.001 1.5 1.5 0 0 0 0-3.001Zm3.87.502a1.248 1.248 0 1 0 0 2.496 1.248 1.248 0 0 0 0-2.496Zm6.88.498h-3l-.102.007a.75.75 0 0 0 0 1.486l.102.007h3l.102-.007a.75.75 0 0 0 0-1.486L18.25 9.5Z"
                                                            fill="#342C82"
                                                        />
                                                    </svg>
                                                    <h3 className={styles.noResultsTitle}>{searchTerm ? "No resource found" : "Start typing to search users"}</h3>
                                                </div>
                                            )}
                                        </TeachingPopoverBody>
                                    </TeachingPopoverSurface>
                                </TeachingPopover>
                            </div>
                            {
                                selectedUsersDisplayMode === "avatar-only"
                                    ? (() => {
                                        const { inlineItems, overflowItems } = partitionAvatarGroupItems({
                                            items: selectedUsers || [],
                                            maxInlineItems: selectedUsersDisplayLimit,
                                        });

                                        return (
                                            selectedUsers && selectedUsers.length > 0 && (
                                                    <div className="AvatarGroupWrapper">
                                                        <AvatarGroup layout="stack" size={18}>
                                                            {inlineItems.map((item) => (
                                                                <HoverCard
                                                                    key={item.id}
                                                                    HoverCardTrigger={
                                                                        <div
                                                                            className={`${styles.userRow} cursor-pointer p-0`}
                                                                            onClick={() => handleRemoveUser(item)}
                                                                        >
                                                                            <Avatar
                                                                                color="colorful"
                                                                                name={item.name}
                                                                                image={{ src: item.image }} />
                                                                        </div>
                                                                    }
                                                                    AvatarId={item.id}
                                                                    AvatarName={item.name}
                                                                    AvatarImage={item.image}
                                                                    AvatarDesignation={item.title}
                                                                    AvatarEmail={item.email}
                                                                    AvatarPhone={item.phoneNumber}
                                                                    AvatarIsExternal={item.isExternal}
                                                                    CanSendMessage={true}
                                                                />
                                                            ))}
                                                            {overflowItems && overflowItems?.length > 0 && (
                                                                <AvatarGroupPopover>
                                                                    <div className={`${styles.userListWrapper} mt-2`}>
                                                                        {overflowItems.map((item) => (
                                                                            <div
                                                                                className={`${styles.userRow} cursor-pointer`}
                                                                                onClick={() => handleRemoveUser(item)}
                                                                            >
                                                                                <HoverCard
                                                                                    key={item.id}
                                                                                    HoverCardTrigger={
                                                                                        <div
                                                                                            className={`${styles.userRow} cursor-pointer p-0`}
                                                                                        >
                                                                                            <Avatar
                                                                                                color="colorful"
                                                                                                name={item.name}
                                                                                                image={{ src: item.image }} />
                                                                                        </div>
                                                                                    }
                                                                                    AvatarId={item.id}
                                                                                    AvatarName={item.name}
                                                                                    AvatarImage={item.image}
                                                                                    AvatarDesignation={item.title}
                                                                                    AvatarEmail={item.email}
                                                                                    AvatarPhone={item.phoneNumber}
                                                                                    AvatarIsExternal={item.isExternal}
                                                                                    CanSendMessage={true}
                                                                                />
                                                                                <p className="text-xsm-400">{item.name}</p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </AvatarGroupPopover>
                                                            )}
                                                        </AvatarGroup>
                                                    </div>
                                            )
                                        );
                                    })()
                                    : selectedUsers.map((user) => (
                                        <div
                                            className={styles.multipleSelectedUsersWrapper}
                                            key={user.id}
                                        >
                                            <Avatar
                                                color="colorful"
                                                name={user.name}
                                                image={{ src: user.image }}
                                            />
                                            <p className="text-xsm-400">{user.name}</p>
                                            <div onClick={() => handleRemoveUser(user)}>
                                                <FilterCross />
                                            </div>
                                        </div>
                                    ))
                            }
                        </div>
                    ) : (
                        selectedUsers.length > 0 ? (
                            <div className={`${styles.singleSelectedUserWrapper} ${errorMessage ? styles.error : ""}`}>
                                <HoverCard
                                    key={selectedUsers[0].id}
                                    HoverCardTrigger={
                                        <div
                                            className={`${styles.userRow} cursor-pointer p-0`}
                                            onClick={() => handleAddUser(selectedUsers[0])}
                                        >
                                            <Avatar
                                                color="colorful"
                                                name={selectedUsers[0].name}
                                                image={{ src: selectedUsers[0].image }} />
                                        </div>
                                    }
                                    AvatarId={selectedUsers[0].id}
                                    AvatarName={selectedUsers[0].name}
                                    AvatarImage={selectedUsers[0].image}
                                    AvatarDesignation={selectedUsers[0].title}
                                    AvatarEmail={selectedUsers[0].email}
                                    AvatarPhone={selectedUsers[0].phoneNumber}
                                    AvatarIsExternal={selectedUsers[0].isExternal}
                                    CanSendMessage={true}
                                />
                                <p className="text-xsm-400">{selectedUsers[0].name}</p>
                                <div onClick={() => handleRemoveUser(selectedUsers[0])}>
                                    <FilterCross />
                                </div>
                            </div>
                        ) : (
                            <TeachingPopover
                                positioning="below-start"
                                withArrow={false}
                                open={openPopover}
                                onOpenChange={(event, data) => !disabled && setIsPopoverOpen(data.open)}
                            >
                                <TeachingPopoverTrigger>
                                    <div className={`${styles.singleUserWrapper} ${errorMessage ? styles.error : ""}`}>
                                        <Button
                                            className={styles.assignAvatarBtn}
                                            onClick={handleOpenPopover}
                                            disabled={disabled}
                                        >
                                            <Avatar
                                                icon={<AddPersonIcon color={"#342c82"} />}
                                                name=""
                                                color="transparent" />
                                            {buttonLabel && (
                                                <p className={`${styles.buttonLabel} text-xsm-400 ms-1 d-flex align-items-center`}>
                                                    {displayButtonLabel}
                                                </p>
                                            )}
                                        </Button>
                                    </div>
                                </TeachingPopoverTrigger>
                                <TeachingPopoverSurface className={styles.assignAvatarSurface}>
                                    <TeachingPopoverBody className={styles.assignAvatarBody}>
                                        <Input placeholder={searchPlaceholder} handleChange={handleSearchChange} />
                                        {isLoading && users.length === 0 ? (
                                            <div className="d-flex justify-content-center mt-4">
                                                <Spin />
                                            </div>
                                        ) : users.length > 0 ? (
                                            <div className={`${styles.userListWrapper} mt-2`}>
                                                {users.map((user) => (
                                                    <div
                                                        className={`${styles.userRow} cursor-pointer`}
                                                        onClick={() => handleAddUser(user)}
                                                    >
                                                        <Avatar
                                                            color="colorful"
                                                            name={user.name}
                                                            image={{ src: user.image }}
                                                        />
                                                        <p className="text-xsm-400">{user.name}</p>
                                                    </div>
                                                ))}
                                                {hasMore && (
                                                    <div className={styles.loadMoreRow}>
                                                        {isLoadingMore ? (
                                                            <Spin />
                                                        ) : (
                                                            <Button onClick={handleLoadMore} className={styles.loadMoreBtn}>
                                                                Load more
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className={styles.noResults}>
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M18.75 4A3.25 3.25 0 0 1 22 7.25v9.505a3.25 3.25 0 0 1-3.25 3.25H5.25A3.25 3.25 0 0 1 2 16.755V7.25a3.25 3.25 0 0 1 3.066-3.245L5.25 4h13.5Zm-9.497 8.5H5.75a.75.75 0 0 0-.743.648L5 13.25v.581c0 1.273.968 1.919 2.501 1.919 1.466 0 2.416-.59 2.496-1.755l.006-.163v-.582a.75.75 0 0 0-.649-.743l-.101-.007Zm2.997 0-1.416-.001c.086.182.142.381.161.591l.008.16v.582l-.01.254a2.85 2.85 0 0 1-.205.899c.108.01.22.015.337.015 1.052 0 1.793-.407 1.869-1.27l.006-.147v-.333a.75.75 0 0 0-.648-.743l-.102-.007-1.416-.001 1.416.001Zm6 0h-3l-.102.007a.75.75 0 0 0 0 1.486l.102.007h3l.102-.007a.75.75 0 0 0 0-1.486l-.102-.007ZM7.5 8.5a1.5 1.5 0 1 0 0 3.001 1.5 1.5 0 0 0 0-3.001Zm3.87.502a1.248 1.248 0 1 0 0 2.496 1.248 1.248 0 0 0 0-2.496Zm6.88.498h-3l-.102.007a.75.75 0 0 0 0 1.486l.102.007h3l.102-.007a.75.75 0 0 0 0-1.486L18.25 9.5Z"
                                                        fill="#342C82"
                                                    />
                                                </svg>
                                                <h3 className={styles.noResultsTitle}>{searchTerm ? "No resource found" : "Start typing to search users"}</h3>
                                            </div>
                                        )}
                                    </TeachingPopoverBody>
                                </TeachingPopoverSurface>
                            </TeachingPopover>
                        )
                    )}
                    {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
                </>
            )}
        </div>
    );
}
